import { useState, useEffect, useCallback } from "react";
import { Paper } from "@mui/material";
import { Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import { getInvoicePaymentSettings, saveInvoicePaymentSettings } from "./Api";

const DAY_OPTIONS = Array.from({ length: 28 }, (_, i) => i + 1);

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const InvoicePaymentSettings = ({ userRole }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [paymentDay, setPaymentDay] = useState(20);
  const [reminderDay, setReminderDay] = useState(5);

  const isCompany = (userRole || "").toLowerCase() === "company";

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getInvoicePaymentSettings();
      const d = res?.data || {};
      setPaymentDay(Number(d.paymentDay) || 20);
      setReminderDay(Number(d.reminderDay) || 5);
    } catch { /* handled */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveInvoicePaymentSettings({ paymentDay, reminderDay });
      toast.success("Payment settings saved");
    } catch { /* handled */ } finally { setSaving(false); }
  };

  const Selector = ({ label, value, onChange, hint, icon, color }) => (
    <div style={{ flex: 1, minWidth: 240 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <div style={{ width: 30, height: 30, borderRadius: 8, background: `${color}1A`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <FeatherIcon icon={icon} size={15} color={color} />
        </div>
        <div style={{ fontSize: "0.82rem", fontWeight: 700, color: "#0F172A" }}>{label}</div>
      </div>
      <select
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        disabled={!isCompany || loading}
        style={{
          width: "100%", border: "1px solid #E2E8F0", borderRadius: 8,
          padding: "9px 12px", fontSize: "0.88rem", fontWeight: 600, color: "#0F172A",
          background: !isCompany ? "#F8FAFC" : "#fff", outline: "none",
          cursor: !isCompany ? "not-allowed" : "pointer",
        }}
      >
        {DAY_OPTIONS.map((d) => (
          <option key={d} value={d}>{ordinal(d)} of the month</option>
        ))}
      </select>
      <div style={{ fontSize: "0.74rem", color: "#64748B", marginTop: 6, lineHeight: 1.5 }}>{hint}</div>
    </div>
  );

  return (
    <Paper elevation={0} className="iv-wrap iv-paper">
      <div className="iv-header">
        <div className="iv-header__left">
          <div className="iv-header__icon-wrap">
            <FeatherIcon icon="calendar" size={18} color="#A5B4FC" />
          </div>
          <div className="iv-header__meta">
            <div className="iv-header__label">Finance</div>
            <div className="iv-header__title">Payment Dates</div>
          </div>
        </div>
      </div>

      <div style={{ padding: "22px 24px" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "30px 0" }}><Spinner style={{ color: "#4F46E5" }} /></div>
        ) : (
          <>
            {!isCompany && (
              <div style={{ padding: "10px 14px", background: "#F8FAFC", color: "#64748B", fontSize: "0.8rem", borderRadius: 8, marginBottom: 18, border: "1px solid #E2E8F0" }}>
                These dates are managed by the company account.
              </div>
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 24, marginBottom: 22 }}>
              <Selector
                label="Usual Payment Date"
                value={paymentDay}
                onChange={setPaymentDay}
                icon="dollar-sign"
                color="#059669"
                hint="The day each month invoices are normally paid. Any unpaid invoice documented on or before this day becomes Overdue after it passes."
              />
              <Selector
                label="Reminder Date"
                value={reminderDay}
                onChange={setReminderDay}
                icon="bell"
                color="#B45309"
                hint="On this day each month, accounting/auditors are emailed a reminder of every invoice still awaiting payment."
              />
            </div>

            <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 10, padding: "12px 16px", fontSize: "0.8rem", color: "#1E40AF", display: "flex", gap: 8, alignItems: "flex-start" }}>
              <FeatherIcon icon="info" size={15} style={{ marginTop: 1, flexShrink: 0 }} />
              <span>
                Invoices documented before the <strong>{ordinal(paymentDay)}</strong> that aren't marked Paid will trigger an accounting reminder on the <strong>{ordinal(reminderDay)}</strong>, and are automatically flagged Overdue once the payment date passes.
              </span>
            </div>

            {isCompany && (
              <div style={{ marginTop: 22, display: "flex", justifyContent: "flex-end" }}>
                <button type="button" className="iv-btn iv-btn--primary" onClick={handleSave} disabled={saving}>
                  {saving ? <Spinner size="sm" /> : <FeatherIcon icon="check" size={14} />}
                  Save Settings
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </Paper>
  );
};

export { InvoicePaymentSettings };
